import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class FuseValidators
{
    /**
     * Check for empty (optional fields) values
     *
     * @param value
     */
    static isEmptyInputValue(value: any): boolean
    {
        return value == null || value.length === 0;
    }

    /**
     * Must match validator
     *
     * @param controlPath A dot-delimited string values that define the path to the control.
     * @param matchingControlPath A dot-delimited string values that define the path to the matching control.
     */
    static mustMatch(controlPath: string, matchingControlPath: string): ValidatorFn
    {
        return (formGroup: AbstractControl): ValidationErrors | null => {

            // Get the control and matching control
            const control = formGroup.get(controlPath);
            const matchingControl = formGroup.get(matchingControlPath);

            // Return if control or matching control doesn't exist
            if ( !control || !matchingControl )
            {
                return null;
            }

            // Delete the mustMatch error to reset the error on the matching control
            if ( matchingControl.hasError('mustMatch') )
            {
                delete matchingControl.errors.mustMatch;
                matchingControl.updateValueAndValidity();
            }

            // Don't validate empty values on the matching control
            // Don't validate if values are matching
            if ( this.isEmptyInputValue(matchingControl.value) || control.value === matchingControl.value )
            {
                return null;
            }

            // Prepare the validation errors
            const errors = {mustMatch: true};

            // Set the validation error on the matching control
            matchingControl.setErrors(errors);

            // Return the errors
            return errors;
        };
    }

    /**
     * Max content size validator, compatible with @ngxmc/file-input's control value
     * (a raw File, an array of Files, or null/undefined — not a wrapper object).
     * Reproduces the same error shape ({ maxContentSize: { actualSize, maxSize } })
     * that ngx-material-file-input's FileValidator.maxContentSize used to produce,
     * so templates reading `errors?.maxContentSize.maxSize` keep working unchanged.
     *
     * @param bytes Max allowed total size, in bytes.
     */
    static maxContentSize(bytes: number): ValidatorFn
    {
        return (control: AbstractControl): ValidationErrors | null => {

            const value = control?.value;
            const files: File[] = value ? (Array.isArray(value) ? value : [value]) : [];
            const size = files.reduce((acc, f) => acc + f.size, 0);

            return bytes >= size
                ? null
                : {maxContentSize: {actualSize: size, maxSize: bytes}};
        };
    }
}
